// Pure helpers for the Workshop 3 dashboard (mirrors workshop1Data.js/workshop2Data.js style).
// Canonical formulas copied from the workshop pages — do not invent a second one:
// - threatLevel = (D*P)/(M*C) with D/P/M/C defaulting to 1 (Activite1 / Activite4).
// - Residual engine reuses the Activite4 formula (Activite4.jsx calculateResidualRisks,
//   StakeholderRiskView.jsx buildStakeholderRows): adjust D/P/M/C by measure
//   effectiveness (D-/P- reduce exposure, M+/C+ raise reliability), abandoned measures
//   contribute 0, clamp each parameter to 1-4, recompute threatLevel, reduction %.

export const DEFAULT_THRESHOLDS = { danger: 3, control: 1.5, watch: 0.5 };

export const ZONE_ORDER = ['danger', 'control', 'watch', 'outside'];

export const ZONE_COLORS = {
  danger: '#DC2626',
  control: '#F59E0B',
  watch: '#0D9488',
  outside: '#9CA3B8'
};

export const PRIORITY_ORDER = ['high', 'medium', 'low'];

export const MEASURE_STATUS_ORDER = ['planned', 'in-progress', 'implemented', 'abandoned'];

export const PRIORITY_WEIGHTS = { high: 3, medium: 2, low: 1 };

export const MEASURE_STATUS_WEIGHTS = { planned: 3, 'in-progress': 2, implemented: 1, abandoned: 0 };

export const TOP_INITIAL_LIMIT = 15;

const numOr = (value, fallback) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

export const resolveThresholds = (thresholds) => ({
  danger: numOr(thresholds && thresholds.danger, DEFAULT_THRESHOLDS.danger),
  control: numOr(thresholds && thresholds.control, DEFAULT_THRESHOLDS.control),
  watch: numOr(thresholds && thresholds.watch, DEFAULT_THRESHOLDS.watch)
});

export const calculateThreatLevel = (stakeholder) => {
  const record = stakeholder && typeof stakeholder === 'object' ? stakeholder : {};
  const d = record.dependency || 1;
  const p = record.penetration || 1;
  const m = record.cyberMaturity || 1;
  const c = record.trust || 1;
  return (d * p) / (m * c);
};

export const resolveStakeholderThreatLevel = (stakeholder) => {
  const stored = Number(stakeholder && stakeholder.threatLevel);
  if (Number.isFinite(stored)) {
    return stored;
  }
  return calculateThreatLevel(stakeholder);
};

export const getThreatZone = (threatLevel, thresholds) => {
  const live = resolveThresholds(thresholds);
  if (threatLevel >= live.danger) {
    return 'danger';
  }
  if (threatLevel >= live.control) {
    return 'control';
  }
  if (threatLevel >= live.watch) {
    return 'watch';
  }
  return 'outside';
};

export const isRetainedStakeholder = (stakeholder) => Boolean(stakeholder && stakeholder.retained);

export const splitRetainedStakeholders = (stakeholders) => {
  const list = Array.isArray(stakeholders) ? stakeholders : [];
  const retained = [];
  const notRetained = [];
  list.forEach((stakeholder) => {
    if (isRetainedStakeholder(stakeholder)) {
      retained.push(stakeholder);
    } else {
      notRetained.push(stakeholder);
    }
  });
  return {
    retained,
    notRetained,
    retainedCount: retained.length,
    totalCount: list.length
  };
};

const stakeholderMatches = (entry, idSet, name) => {
  if (entry === undefined || entry === null) {
    return false;
  }
  if (typeof entry === 'string') {
    return idSet.has(entry) || (name !== '' && entry === name);
  }
  if (typeof entry === 'object') {
    const id = entry.id !== undefined && entry.id !== null ? String(entry.id) : '';
    const altId = entry._id !== undefined && entry._id !== null ? String(entry._id) : '';
    return (id !== '' && idSet.has(id))
      || (altId !== '' && idSet.has(altId))
      || (name !== '' && entry.name === name);
  }
  return false;
};

const pathIdString = (pathId) => {
  if (pathId === undefined || pathId === null) {
    return '';
  }
  if (typeof pathId === 'string') {
    return pathId;
  }
  if (typeof pathId === 'object') {
    const raw = pathId.id !== undefined && pathId.id !== null ? pathId.id : pathId._id;
    return raw !== undefined && raw !== null ? String(raw) : '';
  }
  return String(pathId);
};

// Measures linked to a stakeholder either directly (measure.stakeholders) or via the
// stakeholder's attack paths (measure.attackPaths / measure.attackPathId) — same linkage
// as Activite4's calculateResidualRisks, plus the direct stakeholder link from MeasureForm.
export const getLinkedMeasures = (stakeholder, attackPaths, measures) => {
  if (!stakeholder || typeof stakeholder !== 'object') {
    return [];
  }
  const idSet = new Set(
    [stakeholder.id, stakeholder._id]
      .filter((value) => value !== undefined && value !== null && String(value) !== '')
      .map((value) => String(value))
  );
  const name = stakeholder.name ? String(stakeholder.name) : '';
  const pathList = Array.isArray(attackPaths) ? attackPaths : [];
  const relatedPaths = pathList.filter((path) => {
    if (!path || typeof path !== 'object') {
      return false;
    }
    if (name !== '' && path.stakeholderName === name) {
      return true;
    }
    return Array.isArray(path.stakeholders)
      && path.stakeholders.some((entry) => stakeholderMatches(entry, idSet, name));
  });
  const relatedPathIds = new Set(
    relatedPaths
      .map((path) => {
        const raw = path.id !== undefined && path.id !== null ? path.id : path._id;
        return raw !== undefined && raw !== null ? String(raw) : '';
      })
      .filter((value) => value !== '')
  );
  const measureList = Array.isArray(measures) ? measures : [];
  return measureList.filter((measure) => {
    if (!measure || typeof measure !== 'object') {
      return false;
    }
    const measurePaths = Array.isArray(measure.attackPaths)
      ? measure.attackPaths
      : (measure.attackPathId ? [measure.attackPathId] : []);
    const viaPath = measurePaths.some((pathId) => {
      const key = pathIdString(pathId);
      return key !== '' && relatedPathIds.has(key);
    });
    if (viaPath) {
      return true;
    }
    return Array.isArray(measure.stakeholders)
      && measure.stakeholders.some((entry) => stakeholderMatches(entry, idSet, name));
  });
};

// Abandoned measures contribute 0 (Activite4 getImplementationFactor); every other
// implementation status counts at full effectiveness. Accepts both the `implementation`
// field (ecosystem measures) and a generic `status` field.
export const getAppliedEffectiveness = (measure) => {
  const record = measure && typeof measure === 'object' ? measure : {};
  const abandoned = record.implementation === 'abandoned' || record.status === 'abandoned';
  const factor = abandoned ? 0 : 1;
  return {
    d: (Number(record.dependanceEffectiveness) || 0) * factor,
    p: (Number(record.penetrationEffectiveness) || 0) * factor,
    m: (Number(record.maturiteCyberEffectiveness) || 0) * factor,
    c: (Number(record.confianceEffectiveness) || 0) * factor
  };
};

const clamp14 = (value) => Math.max(1, Math.min(4, value));

// Residual engine — exact copy of the Activite4 formula (see header comment).
export const calculateResidual = (stakeholder, attackPaths = [], measures = []) => {
  const initialD = (stakeholder && stakeholder.dependency) || 1;
  const initialP = (stakeholder && stakeholder.penetration) || 1;
  const initialM = (stakeholder && stakeholder.cyberMaturity) || 1;
  const initialC = (stakeholder && stakeholder.trust) || 1;
  const initialRisk = (initialD * initialP) / (initialM * initialC);
  const linkedMeasures = getLinkedMeasures(stakeholder, attackPaths, measures);

  let currentD = initialD;
  let currentP = initialP;
  let currentM = initialM;
  let currentC = initialC;
  linkedMeasures.forEach((measure) => {
    const eff = getAppliedEffectiveness(measure);
    currentD -= eff.d;
    currentP -= eff.p;
    currentM += eff.m;
    currentC += eff.c;
  });
  currentD = clamp14(currentD);
  currentP = clamp14(currentP);
  currentM = clamp14(currentM);
  currentC = clamp14(currentC);

  const residualRisk = (currentD * currentP) / (currentM * currentC);
  const reduction = initialRisk > 0 ? (initialRisk - residualRisk) / initialRisk : 0;
  return {
    stakeholder,
    initialRisk,
    residualRisk,
    reduction,
    reductionPct: Math.round(reduction * 100),
    linkedMeasures,
    residualParams: {
      dependency: currentD,
      penetration: currentP,
      cyberMaturity: currentM,
      trust: currentC
    }
  };
};

export const buildInitialResidualRows = (stakeholders, attackPaths = [], measures = [], thresholds = null) => {
  const list = Array.isArray(stakeholders) ? stakeholders : [];
  return list
    .filter((stakeholder) => stakeholder && !stakeholder.isCategory && String(stakeholder.name || '').trim() !== '')
    .map((stakeholder) => {
    const computed = calculateResidual(stakeholder, attackPaths, measures);
    return {
      ...computed,
      id: stakeholder && (stakeholder.id || stakeholder._id),
      name: (stakeholder && stakeholder.name) || '',
      type: (stakeholder && stakeholder.type) || '',
      initialZone: getThreatZone(computed.initialRisk, thresholds),
      residualZone: getThreatZone(computed.residualRisk, thresholds)
    };
  });
};

export const selectTopByInitial = (rows, limit = TOP_INITIAL_LIMIT) => [...(Array.isArray(rows) ? rows : [])]
  .sort((a, b) => b.initialRisk - a.initialRisk)
  .slice(0, limit);

export const sortByReductionDesc = (rows) => [...(Array.isArray(rows) ? rows : [])]
  .sort((a, b) => b.reduction - a.reduction);

export const buildScatterPoints = (stakeholders, thresholds = null) => {
  const list = Array.isArray(stakeholders) ? stakeholders : [];
  return list
    .filter((stakeholder) => stakeholder && !stakeholder.isCategory && String(stakeholder.name || '').trim() !== '')
    .map((stakeholder) => {
    const d = (stakeholder && stakeholder.dependency) || 1;
    const p = (stakeholder && stakeholder.penetration) || 1;
    const m = (stakeholder && stakeholder.cyberMaturity) || 1;
    const c = (stakeholder && stakeholder.trust) || 1;
    const threatLevel = (d * p) / (m * c);
    return {
      id: stakeholder && (stakeholder.id || stakeholder._id),
      name: (stakeholder && stakeholder.name) || '',
      exposition: d * p,
      fiabilite: m * c,
      threatLevel,
      zone: getThreatZone(threatLevel, thresholds)
    };
  });
};

// Endpoints of the threat-level threshold line y = x / T clipped to the
// [min, max] x [min, max] box, so the line moves when thresholds change.
export const thresholdLinePoints = (thresholdValue, min = 1, max = 16) => {
  const value = Number(thresholdValue);
  if (!Number.isFinite(value) || value <= 0) {
    return null;
  }
  const candidates = [
    [min, min / value],
    [max, max / value],
    [value * min, min],
    [value * max, max]
  ];
  const inside = candidates.filter(([x, y]) => x >= min && x <= max && y >= min && y <= max);
  const deduped = [];
  inside.forEach(([x, y]) => {
    if (!deduped.some(([px, py]) => Math.abs(px - x) < 1e-9 && Math.abs(py - y) < 1e-9)) {
      deduped.push([x, y]);
    }
  });
  if (deduped.length < 2) {
    return null;
  }
  const sorted = [...deduped].sort((a, b) => a[0] - b[0]);
  return [sorted[0], sorted[sorted.length - 1]];
};

export const buildZoneOriginBreakdown = (stakeholders, thresholds = null) => ZONE_ORDER.map((zone) => {
  const inZone = (Array.isArray(stakeholders) ? stakeholders : [])
    .filter((stakeholder) => getThreatZone(resolveStakeholderThreatLevel(stakeholder), thresholds) === zone);
  return {
    zone,
    internal: inZone.filter((stakeholder) => stakeholder && stakeholder.type === 'internal').length,
    external: inZone.filter((stakeholder) => stakeholder && stakeholder.type !== 'internal').length,
    total: inZone.length
  };
});

const normalizePriority = (raw) => {
  const key = String(raw || '').trim().toLowerCase();
  return PRIORITY_ORDER.includes(key) ? key : null;
};

const normalizeMeasureStatus = (raw) => {
  const key = String(raw || '').trim().toLowerCase();
  return MEASURE_STATUS_ORDER.includes(key) ? key : null;
};

export const getMeasurePriority = (measure) => normalizePriority(measure && measure.priority);

export const getMeasureStatus = (measure) => normalizeMeasureStatus(
  measure && (measure.implementation !== undefined && measure.implementation !== null
    ? measure.implementation
    : measure.status)
);

// Priority x status heatmap. Cell score = count * priorityWeight * statusWeight so that
// "high priority + still planned" reads as alarm on a diverging visualMap; the raw count
// stays available for labels and tooltips. Measures without a valid priority or status
// are reported as unclassified instead of being silently bucketed.
export const buildPriorityStatusMatrix = (measures) => {
  const list = Array.isArray(measures) ? measures : [];
  const cells = {};
  let unclassified = 0;
  let maxScore = 0;
  list.forEach((measure) => {
    const priority = getMeasurePriority(measure);
    const status = getMeasureStatus(measure);
    if (!priority || !status) {
      unclassified += 1;
      return;
    }
    const key = `${status}|${priority}`;
    if (!cells[key]) {
      cells[key] = { priority, status, count: 0, score: 0 };
    }
    cells[key].count += 1;
    cells[key].score = cells[key].count * PRIORITY_WEIGHTS[priority] * MEASURE_STATUS_WEIGHTS[status];
    if (cells[key].score > maxScore) {
      maxScore = cells[key].score;
    }
  });
  const data = [];
  MEASURE_STATUS_ORDER.forEach((status, col) => {
    PRIORITY_ORDER.forEach((priority, row) => {
      const cell = cells[`${status}|${priority}`];
      data.push({ value: [col, row, cell ? cell.score : 0], count: cell ? cell.count : 0 });
    });
  });
  return {
    rowLabels: [...PRIORITY_ORDER],
    columnLabels: [...MEASURE_STATUS_ORDER],
    data,
    maxScore,
    unclassified,
    total: list.length
  };
};

// Suggested thresholds: threat-level percentiles across stakeholders (p90 danger,
// p50 control, p10 watch) — same definition as useAtelier3Store.getEbiosensSuggestedThresholds.
export const computeSuggestedThresholds = (stakeholders) => {
  const levels = (Array.isArray(stakeholders) ? stakeholders : [])
    .map((stakeholder) => resolveStakeholderThreatLevel(stakeholder))
    .filter((level) => Number.isFinite(level))
    .sort((a, b) => a - b);
  if (levels.length === 0) {
    return null;
  }
  const count = levels.length;
  const percentile = (p) => levels[Math.max(0, Math.min(count - 1, Math.round(p * (count - 1))))];
  return {
    danger: percentile(0.9),
    control: percentile(0.5),
    watch: percentile(0.1)
  };
};

// Effectiveness profiles for the stretch radar: average applied effectiveness per axis
// across each stakeholder's linked measures, top N by initial threat.
export const buildEffectivenessProfiles = (stakeholders, attackPaths = [], measures = [], topN = 5) => {
  const rows = buildInitialResidualRows(stakeholders, attackPaths, measures);
  return selectTopByInitial(rows, topN).map((row) => {
    const linked = row.linkedMeasures;
    const totals = { d: 0, p: 0, m: 0, c: 0 };
    linked.forEach((measure) => {
      const eff = getAppliedEffectiveness(measure);
      totals.d += eff.d;
      totals.p += eff.p;
      totals.m += eff.m;
      totals.c += eff.c;
    });
    const divisor = linked.length > 0 ? linked.length : 1;
    return {
      id: row.id,
      name: row.name,
      initialRisk: row.initialRisk,
      measureCount: linked.length,
      d: totals.d / divisor,
      p: totals.p / divisor,
      m: totals.m / divisor,
      c: totals.c / divisor
    };
  });
};

// Orphan-measure sync health: measures linked to no attack path and no stakeholder.
// Such measures never feed the residual engine and signal a sync gap with Activite4.
export const findOrphanMeasures = (measures) => {
  const list = Array.isArray(measures) ? measures : [];
  const orphans = list.filter((measure) => {
    if (!measure || typeof measure !== 'object') {
      return false;
    }
    const measurePaths = Array.isArray(measure.attackPaths)
      ? measure.attackPaths
      : (measure.attackPathId ? [measure.attackPathId] : []);
    const hasPathLink = measurePaths.some((pathId) => pathIdString(pathId) !== '');
    const hasStakeholderLink = Array.isArray(measure.stakeholders) && measure.stakeholders.length > 0;
    return !hasPathLink && !hasStakeholderLink;
  });
  return {
    orphans,
    orphanCount: orphans.length,
    total: list.length
  };
};
